import { ENABLE_BEARER_TOKEN_AUTH } from '@core/constant/featureFlags';
import { SERVER_HOSTS } from '@core/constant/servers';
import { fetchToken } from '@core/util/fetchWithToken';
import { platformFetch } from '@core/util/platformFetch';
import { getMacroApiToken } from '@service-auth/fetch';

// The WhatsApp GraphQL fields live on the same DSS "soup" schema the rest of the
// app uses; we POST hand-written operations to it (no codegen) rather than
// registering them with the normalized urql cache — WhatsApp data is polled, not
// cached. Auth mirrors `graphql-soup.ts`'s `dssGraphqlFetch` exactly.
const GRAPHQL_URL = `${SERVER_HOSTS['document-storage-service']}/items/soup/graphql`;

function mergeHeaders(...headers: Array<HeadersInit | undefined>): Headers {
  const result = new Headers();
  for (const source of headers) {
    if (!source) continue;
    new Headers(source).forEach((value, key) => result.set(key, value));
  }
  return result;
}

async function graphqlFetch(init: RequestInit): Promise<Response> {
  if (ENABLE_BEARER_TOKEN_AUTH) {
    const apiToken = await getMacroApiToken();
    return platformFetch(GRAPHQL_URL, {
      ...init,
      headers: mergeHeaders(init.headers, {
        Authorization: `Bearer ${apiToken}`,
      }),
    });
  }

  const fetchWithCredentials = () =>
    platformFetch(GRAPHQL_URL, { ...init, credentials: 'include' });

  let response = await fetchWithCredentials();
  if (response.status !== 401) return response;

  const tokenResult = await fetchToken();
  if (tokenResult.isErr()) return response;

  return fetchWithCredentials();
}

interface GraphqlResponse<T> {
  data?: T;
  errors?: Array<{ message: string }>;
}

/**
 * Execute a hand-written GraphQL operation against the DSS soup endpoint and
 * return its `data`, throwing on transport or GraphQL errors.
 */
export async function whatsappGraphql<T>(
  query: string,
  variables?: Record<string, unknown>
): Promise<T> {
  const response = await graphqlFetch({
    method: 'POST',
    headers: mergeHeaders({ 'content-type': 'application/json' }),
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    throw new Error(`WhatsApp GraphQL request failed (${response.status})`);
  }

  const payload = (await response.json()) as GraphqlResponse<T>;
  if (payload.errors?.length) {
    throw new Error(payload.errors.map((error) => error.message).join('; '));
  }
  if (!payload.data) {
    throw new Error('WhatsApp GraphQL response contained no data');
  }
  return payload.data;
}
